/**
 * Dialog component — compound component using the native HTML dialog element.
 * Provides modal behavior with backdrop blur, keyboard dismissal, and
 * theme-aware styling matching the platform design system.
 */

import { forwardRef, useEffect, useRef, useCallback } from 'react'
import type { ReactNode } from 'react'
import { clsx } from 'clsx'

/* ── Dialog (root wrapper) ──────────────────────────────────────────── */

export interface DialogProps {
  /** Whether the dialog is open. */
  open: boolean
  /** Called when the dialog open state changes (e.g. user presses Escape). */
  onOpenChange: (open: boolean) => void
  children: ReactNode
}

/**
 * Root dialog wrapper that manages the open/close lifecycle of a native
 * `<dialog>` element rendered by `DialogContent`.
 */
export function Dialog({ open, onOpenChange, children }: DialogProps) {
  return <DialogContext.Provider value={{ open, onOpenChange }}>{children}</DialogContext.Provider>
}

Dialog.displayName = 'Dialog'

/* ── Internal context ───────────────────────────────────────────────── */

import { createContext, useContext } from 'react'

interface DialogContextValue {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const DialogContext = createContext<DialogContextValue | null>(null)

function useDialogContext(): DialogContextValue {
  const ctx = useContext(DialogContext)
  if (!ctx) {
    throw new Error('Dialog compound components must be used within <Dialog>')
  }
  return ctx
}

/* ── DialogContent ──────────────────────────────────────────────────── */

export interface DialogContentProps extends React.HTMLAttributes<HTMLDialogElement> {}

/**
 * The dialog element itself. Renders a native `<dialog>` with modal backdrop
 * and syncs visibility with the parent `Dialog` open prop.
 */
export const DialogContent = forwardRef<HTMLDialogElement, DialogContentProps>(
  ({ className, children, ...props }, forwardedRef) => {
    const { open, onOpenChange } = useDialogContext()
    const internalRef = useRef<HTMLDialogElement>(null)

    // Merge forwarded ref with internal ref
    const dialogRef = (forwardedRef as React.RefObject<HTMLDialogElement>) ?? internalRef
    const resolvedRef = forwardedRef ? dialogRef : internalRef

    useEffect(() => {
      const dialog = resolvedRef.current
      if (!dialog) return

      if (open) {
        if (!dialog.open) {
          dialog.showModal()
        }
      } else {
        if (dialog.open) {
          dialog.close()
        }
      }
    }, [open, resolvedRef])

    const handleCancel = useCallback(
      (e: React.SyntheticEvent<HTMLDialogElement>) => {
        e.preventDefault()
        onOpenChange(false)
      },
      [onOpenChange],
    )

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLDialogElement>) => {
        // Close when clicking the backdrop (the dialog element itself, not children)
        if (e.target === e.currentTarget) {
          onOpenChange(false)
        }
      },
      [onOpenChange],
    )

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- backdrop click-to-close; keyboard dismiss handled by onCancel (Escape)
      <dialog
        ref={resolvedRef}
        className={clsx(
          'bg-surface-elevated border border-border rounded-xl shadow-theme-xl p-0 max-w-lg w-full mx-4',
          'backdrop:bg-black/60 backdrop:backdrop-blur-sm',
          className,
        )}
        onCancel={handleCancel}
        onClick={handleClick}
        {...props}
      >
        <div className="p-6">{children}</div>
      </dialog>
    )
  },
)

DialogContent.displayName = 'DialogContent'

/* ── DialogHeader ───────────────────────────────────────────────────── */

/** Flex column container for dialog title and description. */
export const DialogHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={clsx('flex flex-col gap-1.5', className)} {...props} />
  ),
)

DialogHeader.displayName = 'DialogHeader'

/* ── DialogTitle ────────────────────────────────────────────────────── */

/** Heading for the dialog. */
export const DialogTitle = forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    // eslint-disable-next-line jsx-a11y/heading-has-content -- content provided via spread props (children)
    <h2
      ref={ref}
      className={clsx('text-lg font-semibold text-content-heading tracking-tight', className)}
      {...props}
    />
  ),
)

DialogTitle.displayName = 'DialogTitle'

/* ── DialogDescription ──────────────────────────────────────────────── */

/** Descriptive text beneath the dialog title. */
export const DialogDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={clsx('text-sm text-content-tertiary', className)} {...props} />
))

DialogDescription.displayName = 'DialogDescription'

/* ── DialogFooter ───────────────────────────────────────────────────── */

/** Footer row for dialog action buttons. */
export const DialogFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={clsx('flex flex-row justify-end gap-2 pt-4', className)} {...props} />
  ),
)

DialogFooter.displayName = 'DialogFooter'

/* ── DialogClose ────────────────────────────────────────────────────── */

export interface DialogCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

/** Button that closes the dialog when clicked. */
export const DialogClose = forwardRef<HTMLButtonElement, DialogCloseProps>(
  ({ className, onClick, ...props }, ref) => {
    const { onOpenChange } = useDialogContext()

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onOpenChange(false)
        onClick?.(e)
      },
      [onOpenChange, onClick],
    )

    return (
      <button
        ref={ref}
        type="button"
        className={clsx(
          'inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium',
          'bg-surface-secondary text-content hover:bg-surface-tertiary',
          'transition-colors duration-150',
          className,
        )}
        onClick={handleClick}
        {...props}
      />
    )
  },
)

DialogClose.displayName = 'DialogClose'
